import React, {
  Fragment,
  useState,
  useCallback,
  useMemo,
  memo,
  MouseEvent,
  useContext,
  useEffect,
  useRef,
} from 'react'
import dayjs from 'dayjs'
import tw from 'twin.macro'
import { Button } from 'src/components/shared/Button'
import { Modal, Typography } from '@mui/material'
import { ModalHeader } from 'src/components/shared/ModalHeader'
import { EditFieldForm } from 'src/components/shared/edit-field-form/EditFieldForm'
import { getExpirationMinDate } from 'src/utils/expirationDateUtils'
import { useDispatch, useSelector } from 'react-redux'
import {
  DeclineOnboardingStep,
  FetchRejectionReasonsToDecline,
  RejectOnboarding,
  UpdateCollectData,
} from 'src/constants/actionTypes'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { showToast } from 'src/utils/toast'
import { handleApiError } from 'src/utils/errorHandlers'
import { CollectedData } from 'src/interfaces/collectedData'
import { ContractorDetails } from 'src/interfaces/contractor'
import { GSIcon } from 'src/components/shared/GSIcon'
import { ActionMenu } from 'src/components/shared/ActionMenu'
import { useModalOpen } from 'src/hooks/useModalOpen'
import { MenuActionItem } from 'src/interfaces/ui'
import { colors } from 'src/theme'
import { SensitiveActionModal } from 'src/components/shared/SensitiveActionModal'
import { DeclineReasonModal } from './DeclineReasonModal'
import { getCollectDataStepTitle } from 'src/utils/collectedData'
import { RejectionReason } from 'src/interfaces/rejectionReason'
import { ContractorDetailsModalContext } from '../ContractorDetailsModal.context'
import { CollectedDataStepType } from 'src/constants/collectedData'
import { getUser } from 'src/selectors/user'

enum DateFieldType {
  Expiration = 'expiration',
  EffectiveDate = 'effectiveDate',
}

interface Props {
  collectedData: CollectedData
  contractorDetails: ContractorDetails
  showExpirationOption?: boolean
  showEffectiveDateOption?: boolean
  showDeclineOption?: boolean
  onAction(): void | Promise<void>
}

const StepActionsMenuComponent = ({
  collectedData,
  contractorDetails,
  showDeclineOption,
  showExpirationOption,
  showEffectiveDateOption,
  onAction,
}: Props) => {
  const dispatch = useDispatch()

  const { triggerStatusDelayedContractorRefresh } = useContext(ContractorDetailsModalContext)

  const {
    isOpen: isDeclineReasonOpen,
    open: openDeclineReason,
    close: closeDeclineReason,
  } = useModalOpen()
  const {
    isOpen: isSensitiveModalOpen,
    open: openSensitiveModal,
    close: closeSensitiveModal,
  } = useModalOpen()

  const user = useSelector(getUser)

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [isDeclineLoading, setIsDeclineLoading] = useState(false)
  const [activeDateField, setActiveDateField] = useState<DateFieldType | null>(null)

  const isMountedRef = useRef(true)
  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const closeDateModal = useCallback(() => setActiveDateField(null), [])

  const container = useMemo(() => document.getElementById('root'), [])

  const expirationMinDateOptions = useMemo(
    () => getExpirationMinDate(collectedData.effectiveAt),
    [collectedData.effectiveAt],
  )

  const effectiveDateMinDate = useMemo(() => dayjs().add(1, 'day'), [])

  const effectiveDateMaxDate = useMemo(
    () => (collectedData.expiresAt ? dayjs(collectedData.expiresAt).subtract(1, 'day') : undefined),
    [collectedData.expiresAt],
  )

  const handleClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const handleCloseMenu = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const handleSetExpirationDate = useCallback(
    async (expiresAt: string) => {
      try {
        await createAsyncAction(
          dispatch,
          UpdateCollectData.request({
            stepId: collectedData.collectDataStep.id,
            partnerDriverId: contractorDetails.id,
            expiresAt,
          }),
        )

        if (isMountedRef.current) {
          await onAction()
          closeDateModal()
        }

        showToast('Expiration date has been saved.')
      } catch (err) {
        handleApiError(err)
      }
    },
    [collectedData, contractorDetails, onAction, dispatch, closeDateModal],
  )

  const handleSetEffectiveDate = useCallback(
    async (effectiveAt: string) => {
      try {
        await createAsyncAction(
          dispatch,
          UpdateCollectData.request({
            stepId: collectedData.collectDataStep.id,
            partnerDriverId: contractorDetails.id,
            effectiveAt,
          }),
        )

        if (isMountedRef.current) {
          await onAction()
          closeDateModal()
        }

        showToast('Effective date has been saved.')
      } catch (err) {
        handleApiError(err)
      }
    },
    [collectedData, contractorDetails, onAction, dispatch, closeDateModal],
  )

  const rejectContractor = useCallback(async () => {
    try {
      if (!isMountedRef.current) {
        return
      }

      triggerStatusDelayedContractorRefresh()

      await createAsyncAction(
        dispatch,
        RejectOnboarding.request({ partnerDriverId: contractorDetails.id }),
      )

      if (!isMountedRef.current) {
        return
      }

      await onAction()

      showToast('Step is rejected.')
    } catch (err) {
      handleApiError(err)
    }
  }, [dispatch, contractorDetails, onAction, triggerStatusDelayedContractorRefresh])

  const handleDecline = useCallback(async () => {
    if (collectedData.collectDataStep.type === CollectedDataStepType.InPersonMeeting) {
      rejectContractor()
    } else {
      try {
        const response = await createAsyncAction<RejectionReason[], void>(
          dispatch,
          FetchRejectionReasonsToDecline.request(),
        )

        if (response && response.length > 0) {
          openDeclineReason()
        } else {
          openSensitiveModal()
        }
      } catch (err) {
        handleApiError(err)
        openSensitiveModal()
      }
    }
  }, [collectedData, rejectContractor, dispatch, openDeclineReason, openSensitiveModal])

  const handleDeclineStep = useCallback(
    async (reason: RejectionReason | null, onComplete: () => void) => {
      setIsDeclineLoading(true)

      try {
        if (!isMountedRef.current) {
          return
        }

        await createAsyncAction(
          dispatch,
          DeclineOnboardingStep.request({
            stepId: collectedData.collectDataStep.id,
            partnerDriverId: contractorDetails.id,
            reviewedByEmail: user?.email || '',
            partnerReviewRejectionReasonId: reason?.id,
            declineReason: reason?.name,
          }),
        )

        if (!isMountedRef.current) {
          return
        }

        await onAction()

        showToast('Step is sent to recollection.')
      } catch (err) {
        handleApiError(err)
      }

      if (isMountedRef.current) {
        setIsDeclineLoading(false)
        onComplete()
      }
    },
    [collectedData, contractorDetails, user?.email, dispatch, onAction],
  )

  const handleDeclineReasonConfirm = useCallback(
    async (reason: RejectionReason) => {
      await handleDeclineStep(reason, closeDeclineReason)
    },
    [handleDeclineStep, closeDeclineReason],
  )

  const handleSensitiveModalConfirm = useCallback(async () => {
    await handleDeclineStep(null, closeSensitiveModal)
  }, [handleDeclineStep, closeSensitiveModal])

  const actionItems: Array<MenuActionItem> = useMemo(() => {
    const res = []

    if (showEffectiveDateOption) {
      res.push({
        label: 'Edit effective date',
        icon: <GSIcon iconName="gs_calendar_edit" width={20} />,
        onClick: () => {
          setActiveDateField(DateFieldType.EffectiveDate)
          setAnchorEl(null)
        },
      })
    }

    if (showExpirationOption) {
      res.push({
        label: 'Edit expiration date',
        icon: <GSIcon iconName="gs_calendar_edit" width={20} />,
        onClick: () => {
          setActiveDateField(DateFieldType.Expiration)
          setAnchorEl(null)
        },
      })
    }

    if (showDeclineOption) {
      res.push({
        label: 'Decline',
        icon: <GSIcon iconName="gs_icon_thumbs_down" width={20} />,
        color: colors.COLORS_RED_500,
        onClick: () => {
          handleDecline()
          setAnchorEl(null)
        },
      })
    }

    return res
  }, [showDeclineOption, showExpirationOption, showEffectiveDateOption, handleDecline])

  const isMenuOpen = useMemo(() => Boolean(anchorEl), [anchorEl])

  return (
    <Fragment>
      <Button
        color="grey"
        size="small"
        leftIcon={<GSIcon iconName="gs_dot_3" css={tw`self-center`} width={20} height={20} />}
        onClick={handleClick}
        data-testid="contractor-details-step-actions-trigger-btn"
      />
      <ActionMenu
        anchorEl={anchorEl}
        items={actionItems}
        isOpen={isMenuOpen}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        itemClassName={tw`w-[187px]`}
        onClose={handleCloseMenu}
      />
      <Modal
        container={container}
        open={activeDateField === DateFieldType.Expiration}
        onClose={closeDateModal}
        aria-labelledby="set-expiration-title"
        css={tw`flex justify-center items-center backdrop-blur`}
      >
        <div
          data-testid="contractor-details-step-set-expiration-modal"
          css={tw`bg-white rounded-lg`}
        >
          <ModalHeader
            title="Edit expiration date"
            onClose={closeDateModal}
            closeButtonDataTestId="contractor-details-step-set-expiration-modal-close-btn"
          />
          <EditFieldForm
            label="Select expiration date"
            requiredError="Expiration date is required"
            editFiledType="date"
            minDate={expirationMinDateOptions.minDate}
            minDateError={expirationMinDateOptions.minDateError}
            initialValue={collectedData.expiresAt}
            onSubmit={handleSetExpirationDate}
            onClose={closeDateModal}
            dataTestIdPrefix="contractor-details-step-set-expiration"
          />
        </div>
      </Modal>
      <Modal
        container={container}
        open={activeDateField === DateFieldType.EffectiveDate}
        onClose={closeDateModal}
        aria-labelledby="set-effective-date-title"
        css={tw`flex justify-center items-center backdrop-blur`}
      >
        <div
          data-testid="contractor-details-step-set-effective-date-modal"
          css={tw`bg-white rounded-lg w-[336px]`}
        >
          <ModalHeader
            title="Edit effective date"
            onClose={closeDateModal}
            closeButtonDataTestId="contractor-details-step-set-effective-date-modal-close-btn"
          />
          {collectedData.expiresAt && (
            <div css={tw`flex gap-2 items-center mx-4 mt-4`}>
              <GSIcon
                iconName="gs_triangle_exclamation"
                width={20}
                css={tw`flex-shrink-0 [&>path]:stroke-[rgba(255, 99, 32, 1)]`}
              />
              <Typography variant="body2" color={colors.COLORS_ORANGE_500}>
                {`Please note: the effective date cannot be later than the expiration date (${dayjs(
                  collectedData.expiresAt,
                ).format('MM/DD/YYYY')}).`}
              </Typography>
            </div>
          )}
          <EditFieldForm
            label="Select effective date"
            requiredError="Effective date is required"
            editFiledType="date"
            minDate={effectiveDateMinDate}
            minDateError="Effective date should be at least 1 day in the future"
            maxDate={effectiveDateMaxDate}
            initialValue={collectedData.effectiveAt}
            onSubmit={handleSetEffectiveDate}
            onClose={closeDateModal}
            dataTestIdPrefix="contractor-details-step-set-effective-date"
          />
        </div>
      </Modal>
      <DeclineReasonModal
        isOpen={isDeclineReasonOpen}
        onClose={closeDeclineReason}
        onConfirm={handleDeclineReasonConfirm}
        isLoading={isDeclineLoading}
      />
      <SensitiveActionModal
        isOpen={isSensitiveModalOpen}
        title="Recollect step?"
        text={`Are you sure you want to initiate ${getCollectDataStepTitle(
          collectedData,
        )} recollection?`}
        submitLabel="Yes"
        onClose={closeSensitiveModal}
        onSubmit={handleSensitiveModalConfirm}
      />
    </Fragment>
  )
}

export const StepActionsMenu = memo(StepActionsMenuComponent)
